import { z } from 'zod';

import { ClientIdSchema, PhotoUploadSchema } from './requests';
import {
  AccessStatusSchema,
  AddressSchema,
  IdSchema,
  MergeFieldSchema,
  MemberSchema,
  MinistryIconKeySchema,
  OrganizationSchema,
  OrgSlugSchema,
  RegistrationDataSchema,
  RegistrationSchema,
} from './resources';

/** Organizações (Administrador da plataforma), busca pública do login e cadastro público. */

const UfSchema = z.union([z.literal(''), z.string().regex(/^[A-Z]{2}$/)]);

// ===== Busca pública (login) =====

export const PublicOrgSchema = OrganizationSchema.pick({
  name: true,
  slug: true,
  city: true,
  uf: true,
  color: true,
  status: true,
})
  .extend({
    registrationToken: z.string().nullable().meta({
      description:
        'Token do link de cadastro quando ele está recebendo cadastros (o "Fazer cadastro" do login).',
    }),
  })
  .meta({ id: 'PublicOrg', description: 'O que o login mostra de uma organização.' });

export const PublicOrgListSchema = z
  .object({ items: z.array(PublicOrgSchema) })
  .meta({ id: 'PublicOrgList' });

export const PublicOrgQuerySchema = z.object({
  q: z
    .string()
    .trim()
    .min(2)
    .max(80)
    .meta({ description: 'Nome, endereço de acesso ou cidade (2 letras ou mais).' }),
});

// ===== Gestão (plataforma) =====

export const OrgSummarySchema = OrganizationSchema.extend({
  stats: z.object({
    members: z.number().int(),
    ministries: z.number().int(),
    events: z.number().int(),
    pendingRegistrations: z.number().int(),
  }),
  admin: z
    .object({ memberId: IdSchema, name: z.string(), email: z.string(), status: AccessStatusSchema })
    .nullable()
    .meta({ description: 'O primeiro Administrador da organização e a situação do acesso dele.' }),
}).meta({ id: 'OrgSummary', description: 'Card da página Organizações.' });

export const OrgListSchema = z.object({ items: z.array(OrgSummarySchema) }).meta({ id: 'OrgList' });

export const OrgCreateSchema = z
  .object({
    name: z.string().trim().min(1).max(80),
    slug: OrgSlugSchema,
    city: z.string().trim().max(80),
    uf: UfSchema,
    address: AddressSchema.optional(),
    color: z.string().regex(/^#[0-9A-F]{6}$/i),
    admin: z.object({ name: z.string().trim().min(1).max(120), email: z.email().max(254) }),
    suggested: z.boolean().meta({ description: 'Começar com os ministérios e funções sugeridos.' }),
  })
  .meta({ id: 'OrgCreate', description: 'Nova organização; o administrador recebe o convite por e-mail.' });

export const OrgPatchSchema = z
  .object({
    name: z.string().trim().min(1).max(80),
    slug: OrgSlugSchema.meta({ description: 'Só o Administrador da plataforma muda.' }),
    city: z.string().trim().max(80),
    uf: UfSchema,
    address: AddressSchema.meta({ description: 'O endereço muda a coordenada: o servidor busca a nova.' }),
    color: z.string().regex(/^#[0-9A-F]{6}$/i),
  })
  .partial()
  .meta({ id: 'OrgPatch' });

export const OrgStatusRequestSchema = z
  .object({ status: z.enum(['active', 'suspended']) })
  .meta({ id: 'OrgStatusRequest', description: 'Suspender (bloqueia o login) ou reativar.' });

export const OrgAdminInviteRequestSchema = z
  .object({
    email: z.email().max(254).optional().meta({
      description: 'Corrige o e-mail do administrador antes de enviar (só enquanto ele não criou a senha).',
    }),
  })
  .meta({ id: 'OrgAdminInviteRequest', description: 'Reenviar o convite do administrador da organização.' });

export const SlugQuerySchema = z.object({ slug: z.string().max(80), orgId: IdSchema.optional() });

export const SlugCheckSchema = z
  .object({ available: z.boolean(), error: z.string().nullable() })
  .meta({ id: 'SlugCheck', description: 'Se o endereço tem o formato certo e está livre.' });

// ===== Cadastro público =====

export const RegistrationFormSchema = z
  .object({
    org: PublicOrgSchema.omit({ registrationToken: true }),
    open: z.boolean().meta({ description: 'Falso: link pausado — "Cadastros encerrados".' }),
    ministries: z.array(
      z.object({ id: IdSchema, name: z.string(), color: z.string(), icon: MinistryIconKeySchema })
    ),
  })
  .meta({ id: 'RegistrationForm', description: 'O que o formulário público precisa para abrir.' });

export const RegistrationSubmitSchema = z
  .object({
    data: RegistrationDataSchema,
    photo: PhotoUploadSchema.optional().meta({
      description: 'A foto da pessoa (opcional), já recortada e reduzida como a do perfil.',
    }),
    consent: z.literal(true).meta({ description: 'Autorização da LGPD — obrigatória para enviar.' }),
  })
  .meta({ id: 'RegistrationSubmit' });

export const ApproveRegistrationSchema = z
  .object({
    memberId: ClientIdSchema.unwrap().meta({
      description: 'Id do membro que a aprovação cria (gerado pela interface).',
    }),
  })
  .meta({ id: 'ApproveRegistration' });

export const MergeRegistrationSchema = z
  .object({
    memberId: IdSchema.meta({ description: 'O cadastro existente da mesma pessoa.' }),
    fields: z
      .array(MergeFieldSchema)
      .max(14)
      .meta({ description: 'Os dados em que vale o da solicitação; nos demais, fica o do cadastro.' }),
  })
  .meta({ id: 'MergeRegistration' });

export const RejectRegistrationSchema = z
  .object({ reason: z.string().max(300).default('') })
  .meta({ id: 'RejectRegistration' });

export const RegistrationDecisionSchema = z
  .object({ registration: RegistrationSchema, member: MemberSchema.nullable() })
  .meta({ id: 'RegistrationDecision' });

export const RegistrationLinkPatchSchema = z
  .object({ active: z.boolean() })
  .meta({ id: 'RegistrationLinkPatch', description: 'Pausar ou retomar os cadastros.' });

export type PublicOrg = z.infer<typeof PublicOrgSchema>;
export type OrgSummary = z.infer<typeof OrgSummarySchema>;
export type OrgCreate = z.infer<typeof OrgCreateSchema>;
export type OrgPatch = z.infer<typeof OrgPatchSchema>;
export type OrgAdminInviteRequest = z.infer<typeof OrgAdminInviteRequestSchema>;
export type RegistrationForm = z.infer<typeof RegistrationFormSchema>;
export type RegistrationSubmit = z.infer<typeof RegistrationSubmitSchema>;
